import { Chip, Stack } from "@mui/material";
import { useId, useState } from "react";
import { Button, Card, Icon, Typography } from "@platform/design-system";

import { formatRunDuration, formatTokenCount } from "../../format";
import { RunStateCards } from "../RunStateCards";

import type { AtAGlance, RunMeta, RunStateCard } from "../../types";

/** A label above its value, the shape every run stat and fact uses. */
const Stat = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <Stack spacing={0.25}>
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
    {children}
  </Stack>
);

const StatValue = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => (
  <Typography variant="subtitle2">{children}</Typography>
);

const NOT_STATED = "Not stated";
const NO_NARRATIVE = "Rumi wrote nothing about this RFP as a whole.";
const NARRATIVE_PREVIEW_LENGTH = 500;

/**
 * The narrative cut at the last whitespace before the preview length. When
 * nothing but whitespace comes before that break, the preview keeps the full
 * preview length rather than collapsing to nothing.
 */
const previewOf = (narrative: string): string => {
  const head = narrative.slice(0, NARRATIVE_PREVIEW_LENGTH);
  const lastBreak = head.search(/\s\S*$/);
  const beforeBreak = lastBreak === -1 ? "" : head.slice(0, lastBreak);
  const hasWordBeforeBreak = /\S/.test(beforeBreak);
  const cut = hasWordBeforeBreak ? beforeBreak : head;
  return `${cut.trimEnd()}…`;
};

/**
 * Rumi's own words about the run, collapsed to a preview when long runs would
 * otherwise make the card very tall.
 */
const Narrative = ({
  narrative,
}: {
  narrative: string | null;
}): React.JSX.Element => {
  const narrativeId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const isLong =
    narrative !== null && narrative.length > NARRATIVE_PREVIEW_LENGTH;
  const isCollapsed = isLong && !isExpanded;

  return (
    <Stack
      spacing={1}
      alignItems="flex-start"
      sx={{ flex: 1, maxWidth: "75ch" }}
    >
      <Typography variant="body-medium" id={narrativeId}>
        {isCollapsed ? previewOf(narrative) : (narrative ?? NO_NARRATIVE)}
      </Typography>
      {isLong && (
        <Button
          variant="tertiary"
          size="sm"
          className="no-uppercase"
          endIcon={<Icon name={isExpanded ? "collapse" : "expand"} />}
          aria-expanded={isExpanded}
          aria-controls={narrativeId}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "Show Less" : "Show More"}
        </Button>
      )}
    </Stack>
  );
};

/** A row of coverage or carrier names. */
const Tags = ({ names }: { names: readonly string[] }): React.JSX.Element => (
  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
    {names.map((name) => (
      <Chip key={name} label={name} size="small" variant="outlined" />
    ))}
  </Stack>
);

/** A labeled row of carrier names. */
const CarrierRow = ({
  label,
  names,
}: {
  label: string;
  names: readonly string[];
}): React.JSX.Element => (
  <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
    <Typography variant="subtitle2">{label}</Typography>
    <Tags names={names} />
  </Stack>
);

interface Props {
  narrative: string | null;
  meta: RunMeta;
  atAGlance: AtAGlance;
  stateCards: readonly RunStateCard[];
  onRerun: () => void;
}

/**
 * Rumi's account of the run: what it did in its own words, the facts it
 * established beside that, and the three counts those add up to.
 */
const RumisRead = ({
  narrative,
  meta,
  atAGlance,
  stateCards,
  onRerun,
}: Props): React.JSX.Element => (
  <Card
    title={
      <Stack direction="row" spacing={1} alignItems="center">
        <Icon name="ai" size="md" color="secondary" />
        <span>Rumi&apos;s Read</span>
      </Stack>
    }
    action={
      <Button variant="primary" size="sm" onClick={onRerun}>
        Run Rumi
      </Button>
    }
    variant="outlined"
  >
    <Stack spacing={3}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
        <Narrative narrative={narrative} />

        <Stack spacing={2} sx={{ flex: 1 }}>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Stat label="Run time">
              <StatValue>{formatRunDuration(meta.durationSeconds)}</StatValue>
            </Stat>
            {meta.cost && (
              <>
                <Stat label="Tokens">
                  <StatValue>
                    {formatTokenCount(meta.cost.inputTokens)} in ·{" "}
                    {formatTokenCount(meta.cost.outputTokens)} out
                  </StatValue>
                </Stat>
                <Stat label="Model">
                  <StatValue>{meta.cost.model}</StatValue>
                </Stat>
              </>
            )}
          </Stack>

          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Stat label="Situs">
              <StatValue>{atAGlance.situsState ?? NOT_STATED}</StatValue>
            </Stat>
            <Stat label="Eligible Employees">
              <StatValue>{atAGlance.eligibleEmployees ?? NOT_STATED}</StatValue>
            </Stat>
          </Stack>

          <Stat label={`${atAGlance.coverages.length} Coverages`}>
            <Tags names={atAGlance.coverages} />
          </Stat>

          <Stat
            label={`${atAGlance.incumbentCarriers.length + atAGlance.newCarriers.length} Carriers`}
          >
            <Stack spacing={0.5}>
              <CarrierRow
                label="Incumbent"
                names={atAGlance.incumbentCarriers}
              />
              <CarrierRow label="New" names={atAGlance.newCarriers} />
            </Stack>
          </Stat>
        </Stack>
      </Stack>

      <RunStateCards cards={stateCards} />
    </Stack>
  </Card>
);

export { RumisRead };
export type { Props as RumisReadProps };
